import { cardPreviewContext } from '@context/card-preview-context';
import { useQuery } from '@hooks/compat/use-query';
import { useCardAttribute } from '@hooks/use-card-attributes';
import { Box } from '@ui-lib';
import { type PropsWithChildren, useContext, useEffect, useState } from 'react';

import { SingleInputCardProperty } from '../SingleInputCardProperty';
import styles from './CompanyName.module.scss';
import type { CardProperty } from '.cache/__types__';

type CompanyNameProps = {
  property?: CardProperty;
  // In future, cardId could come from the cardFieldEditContext (or similar context), that way we can tell if there is no
  // cardId, that we need to create a new card before adding the property.
  cardId: string;
};

export const CompanyName: React.FC<CompanyNameProps> = ({ property, cardId }) => {
  const { hideEmptyAttributes } = useContext(cardPreviewContext);
  const [isVisible, setIsVisible] = useState(false);
  const { searchParams } = useQuery();

  useEffect(() => {
    const fromSample = searchParams?.get('fromSample');
    setIsVisible(!!fromSample);
  }, [searchParams]);

  const cardAttributeType = useCardAttribute('org_name');
  if (!cardAttributeType) {
    return null;
  }

  // if hideEmptyAttributes specified as true and property value empty, then company name shouldn't be shown on the card at all
  if (hideEmptyAttributes && !property?.value) {
    return <></>;
  }

  return (
    // eslint-disable-next-line jsx-a11y/click-events-have-key-events, jsx-a11y/no-static-element-interactions
    <div className={styles['user-company-container']} onClick={() => setIsVisible(false)}>
      <Tooltip isVisible={isVisible}>Click on the field to start editing</Tooltip>
      <SingleInputCardProperty
        attribute={cardAttributeType}
        cardId={cardId}
        className={styles['user-company']}
        inputLabel="Company Name"
        property={property}
        defaultTemplateValueProvider={'template'}
      />
    </div>
  );
};
interface TooltipContainerProps {
  isVisible: boolean;
  backgroundColor?: string;
}
function Tooltip({
  children,
  isVisible,
  backgroundColor = '#314079',
}: PropsWithChildren<TooltipContainerProps>) {
  return (
    <Box
      position="absolute"
      display={isVisible ? 'block' : 'none'}
      style={{
        zIndex: 3,
        color: 'white',
        padding: '0.5rem',
        borderRadius: '0.25rem',
        top: '80%',
        left: '0',
        marginLeft: 'auto',
        marginRight: 'auto',
        maxWidth: 'fit-content',
      }}
    >
      <TooltipArrow backgroundColor={backgroundColor} />
      <TooltipContent backgroundColor={backgroundColor}>{children}</TooltipContent>
    </Box>
  );
}

interface TooltipStyleProps {
  backgroundColor: string;
}
function TooltipContent({ children, backgroundColor }: PropsWithChildren<TooltipStyleProps>) {
  return (
    <Box
      style={{
        backgroundColor,
        color: 'white',
        padding: '0.5rem 1rem',
        borderRadius: '0.25rem',
      }}
    >
      {children}
    </Box>
  );
}

function TooltipArrow({ backgroundColor }: TooltipStyleProps) {
  return (
    <Box
      style={{
        width: '0',
        height: '0',
        marginLeft: '15%',
        marginRight: 'auto',
        borderLeft: '6px solid transparent',
        borderRight: '6px solid transparent',
        borderBottom: `6px solid ${backgroundColor}`,
      }}
    />
  );
}
